import {
    CityMultiLingualDTO,
    CityWithStreetsDTO,
    Street,
} from "@baloise-cfa/tsclient/addr";
import { useEffect, useState } from "react";

import { useFlowNavigation } from "../useFlowNavigation";

import { useDebouncedEffect } from "./../useDebouncedEffect";

import { PageNames } from "@enums";

export function useStreetSearch(
    typeAheadStreet: string,
    propertyCity?: CityMultiLingualDTO,
    propertyStreet?: Street,
) {
    const isSSR = typeof window === "undefined";
    const [streetResults, setStreetResults] = useState<
        Array<Street> | undefined
    >([]);
    const [streetsLoading, setStreetsLoading] = useState<boolean>(false);
    const { navigateTo } = useFlowNavigation();

    const getStreetsFromData = (postalCode: string, searchString: string) => {
        // We are not using the AddressApiClient here because it is endoflife
        // and this endpoint is not included in the API client
        setStreetsLoading(true);
        fetch(
            `/api/address/postalcodes/${postalCode}/streets?streetName=${searchString}`,
        )
            .then((response) => response.json())
            .then((response: CityWithStreetsDTO) => {
                if (response) {
                    if (Array.isArray(response)) {
                        setStreetResults(
                            response?.reduce<Street[]>((acc, city) => {
                                if (city.streets) {
                                    return acc.concat(city.streets);
                                }
                                return acc;
                            }, []),
                        );
                    } else {
                        setStreetResults(response.streets);
                    }
                }
            })
            .catch((error) => {
                setStreetResults(undefined);
                console.error(error);
                navigateTo(PageNames.TechnicalDifficulties);
            });
    };

    useEffect(() => {
        if (
            (streetResults && streetResults.length > 0) ||
            ((!streetResults || streetResults.length === 0) &&
                typeAheadStreet === "")
        ) {
            setStreetsLoading(false);
        }
    }, [streetResults, typeAheadStreet]);

    useDebouncedEffect(
        () => {
            let isAlreadyFetched = false;
            if (
                !isSSR &&
                typeAheadStreet.length > 2 &&
                propertyCity?.postalCode
            ) {
                getStreetsFromData(propertyCity.postalCode, typeAheadStreet);
                isAlreadyFetched = true;
            } else if (
                !isSSR &&
                propertyCity?.postalCode &&
                propertyStreet?.name &&
                propertyStreet.name.length !== 0 &&
                typeAheadStreet.length === 0 &&
                streetResults?.length === 0
            ) {
                getStreetsFromData(
                    propertyCity.postalCode,
                    propertyStreet.name,
                );
                isAlreadyFetched = true;
            }

            if (
                !isSSR &&
                typeAheadStreet === "" &&
                !isAlreadyFetched &&
                !propertyStreet?.name
            ) {
                setStreetResults(undefined);
            }
            // eslint-disable-next-line
        },
        300,
        [
            isSSR,
            typeAheadStreet,
            propertyCity,
            propertyStreet,
            streetResults?.length,
        ],
    );

    return { streetResults, streetsLoading };
}
